// Small per-user localStorage cache for stale-while-revalidate rendering:
// screens paint the last known data instantly, then replace it with the fresh
// network response. It is never the source of truth.
//
// - Keys are namespaced per user, so switching accounts never shows another
//   user's messages; everything is wiped on logout.
// - Entries expire after MAX_AGE_MS.
// - Writes are deferred to idle time and coalesced per key, so saving never
//   blocks scrolling or typing. Reads see pending writes immediately.
// - Entry timestamps live in a small index, so pruning/eviction never has to
//   parse cached payloads (which blocked the UI for seconds with large caches).
// - On quota errors the oldest entries are evicted and the write retried once.
// - Every storage access is wrapped: private mode / blocked storage just
//   means no cache.

const PREFIX = 'bzc:v1:';
const INDEX_KEY = `${PREFIX}__index`;
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

const currentUserId = () => {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null')?.id ?? null;
  } catch {
    return null;
  }
};

const fullKey = (key) => {
  const uid = currentUserId();
  return uid == null ? null : `${PREFIX}${uid}:${key}`;
};

// Drop null/undefined fields recursively to keep entries slim. Empty arrays
// and strings are kept, since render code may rely on them being present.
export const slim = (value) => {
  if (Array.isArray(value)) return value.map(slim);
  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      if (v !== null && v !== undefined) out[k] = slim(v);
    }
    return out;
  }
  return value;
};

// ── Timestamp index: { [fullKey]: writtenAt } ────────────────────────
const readIndex = () => {
  try {
    return JSON.parse(localStorage.getItem(INDEX_KEY) || '{}') || {};
  } catch {
    return {};
  }
};

const writeIndex = (index) => {
  try {
    localStorage.setItem(INDEX_KEY, JSON.stringify(index));
  } catch { /* ignore */ }
};

const removeEntries = (keys, index) => {
  keys.forEach((k) => {
    try { localStorage.removeItem(k); } catch { /* ignore */ }
    delete index[k];
  });
};

/** All cached keys, oldest first. Keys missing from the index count as oldest. */
const entriesOldestFirst = (index) => {
  const keys = new Set(Object.keys(index));
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k?.startsWith(PREFIX) && k !== INDEX_KEY) keys.add(k);
    }
  } catch { /* storage unavailable */ }
  return [...keys].map((k) => ({ k, t: index[k] || 0 })).sort((a, b) => a.t - b.t);
};

// ── Deferred writes ──────────────────────────────────────────────────
const pendingWrites = new Map(); // fullKey -> { t, d }
const pendingPrunes = new Map(); // full key prefix -> max entries
let flushScheduled = false;

const scheduleFlush = () => {
  if (flushScheduled) return;
  flushScheduled = true;
  const run = () => {
    flushScheduled = false;
    flushCacheWrites();
  };
  if (typeof window !== 'undefined' && window.requestIdleCallback) {
    window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 300);
  }
};

/** Write everything pending now (called from idle time; exported for tests). */
export const flushCacheWrites = () => {
  if (pendingWrites.size === 0 && pendingPrunes.size === 0) return;
  const index = readIndex();

  pendingWrites.forEach((entry, k) => {
    let raw;
    try {
      raw = JSON.stringify(entry);
    } catch {
      return;
    }
    try {
      localStorage.setItem(k, raw);
      index[k] = entry.t;
    } catch {
      // Most likely quota exceeded: evict the oldest ~30% and retry once
      const entries = entriesOldestFirst(index).filter((e) => e.k !== k);
      removeEntries(entries.slice(0, Math.max(1, Math.ceil(entries.length * 0.3))).map((e) => e.k), index);
      try {
        localStorage.setItem(k, raw);
        index[k] = entry.t;
      } catch { /* give up silently */ }
    }
  });
  pendingWrites.clear();

  pendingPrunes.forEach((max, base) => {
    const matching = entriesOldestFirst(index).filter(({ k }) => k.startsWith(base)).reverse();
    removeEntries(matching.slice(max).map((e) => e.k), index);
  });
  pendingPrunes.clear();

  writeIndex(index);
};

// ── Public API ───────────────────────────────────────────────────────
export const readCache = (key) => {
  const k = fullKey(key);
  if (!k) return null;
  const pending = pendingWrites.get(k);
  if (pending) return pending.d;
  try {
    const raw = localStorage.getItem(k);
    if (!raw) return null;
    const { t, d } = JSON.parse(raw);
    if (!t || Date.now() - t > MAX_AGE_MS) {
      localStorage.removeItem(k);
      return null;
    }
    return d;
  } catch {
    return null;
  }
};

export const writeCache = (key, data) => {
  const k = fullKey(key);
  if (!k) return;
  pendingWrites.set(k, { t: Date.now(), d: slim(data) });
  scheduleFlush();
};

// Keep only the `max` most recently written keys that start with `keyPrefix`
// (e.g. cap cached message threads to the last N opened). Runs with the next flush.
export const pruneCache = (keyPrefix, max) => {
  const base = fullKey(keyPrefix);
  if (!base) return;
  pendingPrunes.set(base, max);
  scheduleFlush();
};

if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    try { flushCacheWrites(); } catch { /* ignore */ }
  });
}

export const clearCache = () => {
  pendingWrites.clear();
  pendingPrunes.clear();
  const index = readIndex();
  removeEntries(entriesOldestFirst(index).map((e) => e.k), index);
  try { localStorage.removeItem(INDEX_KEY); } catch { /* ignore */ }
};
